import useApp from 'hooks/useApp';
import useData from 'hooks/useData';
import { memo, useMemo } from 'react';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import ingkaSizes from '@ingka/variables/design-tokens';
import { convertColour } from 'utils/canvas';
import { LoadingWrapper } from 'components/LoadingIndicator/LoadingWrapper';
import { colourStaticBlack } from '@ingka/variables/colours-css';
import type { TFunction } from 'i18next';
import { getAlternatingColor } from 'utils/colors';
import { toIntlLongMonthFullYear } from 'utils/date';
import moment from 'moment';
import { roundDisplayValueDoubleDecimal } from 'utils/number';
import { displayRange } from 'components/ContractMix/ContractMix';
import Graph from './Graph';
import GraphHeader from './GraphHeader';
import { ChartDataWithLabel, ChartGeneratorOptions, GraphData } from './types';
import GraphLegend from './GraphLegend';
import classes from './Graph.module.scss';

const emptyDataset = (t: TFunction<'translation', 'global', 'translation'>) => [{
    label: t('no_data'),
    data: [],
}];
const datasetIsEmpty = (data: ChartDataWithLabel[]) => data.length === 1 && data[0].data.length === 0;

const graphOptions = (
    t: TFunction<'translation', 'overview.contractmixevolution', 'translation'> & {
        global: TFunction<'translation', 'global', 'translation'>;
    },
    graphData: GraphData,
): ChartGeneratorOptions => (
    {
        layout: {
            padleft: datasetIsEmpty(graphData.datasets) ? 0 : 20,
        },
        scales: {
            tickColor: convertColour(colourStaticBlack),
            weeksThreshold: 16,
            labels: graphData.labels,
            hasNegativeValues: graphData.datasets.some(ds => ds.data.some(d => d < 0)),
            formatterYTicks: roundDisplayValueDoubleDecimal
        },
        plugins: {
            customYLabel: {
                text: '%',
                offsetX: 24,
                color: convertColour(colourStaticBlack),
            },
            emptyGraph: {
                text: t.global('no_data')
            },
            tooltip: {
                external: {
                    tooltipDivId: 'contract-mix-evolution-tooltip',
                    showAbovePointer: true,
                    titleFormatter: (currentTitle: string) => (
                        `${t.global('week')} ${currentTitle.slice(-2)} (${toIntlLongMonthFullYear(moment(currentTitle).toDate())})`
                    ),
                    numberFormatter: roundDisplayValueDoubleDecimal,
                },
            }
        }

    }
);

export const ContractMixGraph = memo(() => {
    const { gridData, timeSelection } = useApp();
    const { isFetching, currentUnit, orgData } = useData();
    const t = useNestedTranslation('overview.contractmixevolution');

    const graphData = useMemo<GraphData>(() => {
        if (!gridData?.contractMixData || !currentUnit || !orgData) {
            return {
                datasets: emptyDataset(t.global),
                labels: timeSelection.timeArray,
            };
        }

        const newDataSets: ChartDataWithLabel[] = gridData.contractMixData
            .filter(data => data.numberOfCoworkers.some(coworker => coworker > 0))
            .map((cmd, index) => ({
                type: 'line' as const,
                label: displayRange(cmd.rangeData, t.global),
                data: cmd.fractionOfCoworkers.map(fraction => fraction * 100),
                backgroundColor: getAlternatingColor(index),
                spanGaps: true,
                segment: {
                    borderColor: getAlternatingColor(index),
                    backgroundColor: getAlternatingColor(index),
                    borderWidth: 2, // px
                },
                pointRadius: 0, // px
                borderColor: getAlternatingColor(index),
            }));

        return {
            datasets: newDataSets,
            labels: timeSelection.timeArray
        };
    }, [currentUnit, gridData, orgData, t, timeSelection.timeArray]);

    return (
        <LoadingWrapper
            className={classes['loading-wrapper-status']}
            isLoading={isFetching}
        >
            <>
                <div className={classes['status-header-container']}>
                    <GraphHeader
                        headerTitle={t('contract_mix_evolution_graph_title')}
                        headerSubTitle={t('contract_mix_evolution_graph_subtitle')}
                    />
                </div>
                <Graph
                    graphData={graphData}
                    graphOptions={graphOptions(t, graphData)}
                    customOptions={{ height: ingkaSizes.space1350 }}
                />
                {(graphData.datasets.length > 0 && graphData.datasets[0].label !== t.global('no_data')) && (
                <GraphLegend
                    legends={graphData.datasets.map(el => ({
                        label: el.label,
                        color: el.backgroundColor as string
                    }))}
                />
                )}
            </>
        </LoadingWrapper>
    );
});
